// src/default-dict.ts
function defaultDict(defaultFactory) {
  return new Proxy(
    {},
    {
      get: (target, p) => {
        if (!(p in target)) {
          target[p] = defaultFactory();
        }
        return target[p];
      }
    }
  );
}

// src/default-map.ts
var DefaultMap = class extends Map {
  constructor(defaultFn, entries) {
    super(entries);
    this.defaultFn = defaultFn;
  }
  get(key) {
    if (!this.has(key)) {
      this.set(key, this.defaultFn(key));
    }
    return super.get(key);
  }
};
var UncheckedMap = class extends DefaultMap {
  constructor(entries) {
    super((key) => {
      throw new Error("UncheckedMap", { cause: { key } });
    }, entries);
  }
};
var HashKeyMap = class {
  constructor(keyFn = JSON.stringify) {
    this.keyFn = keyFn;
  }
  map = /* @__PURE__ */ new Map();
  get(key) {
    return this.map.get(this.keyFn(key))?.[1];
  }
  set(key, value) {
    this.map.set(this.keyFn(key), [key, value]);
    return this;
  }
  delete(key) {
    return this.map.delete(this.keyFn(key));
  }
  has(key) {
    return this.map.has(this.keyFn(key));
  }
  clear() {
    this.map.clear();
  }
  entries() {
    return this.map.values();
  }
};
var HashKeyDefaultMap = class extends HashKeyMap {
  constructor(defaultFn, keyFn) {
    super(keyFn);
    this.defaultFn = defaultFn;
  }
  get(key) {
    if (!this.has(key)) {
      this.set(key, this.defaultFn(key));
    }
    return super.get(key);
  }
};

// src/option.ts
function mapOption(x, f) {
  return typeof x === "undefined" ? x : f(x);
}

// src/result.ts
function Ok(value) {
  return { ok: true, value };
}
function Err(value) {
  return { ok: false, value };
}
function wrapError(getValue) {
  try {
    return Ok(getValue());
  } catch (e) {
    return Err(e);
  }
}
async function wrapErrorAsync(getValue) {
  try {
    return Ok(await getValue());
  } catch (e) {
    return Err(e);
  }
}
function okToOption(result) {
  return result.ok ? result.value : void 0;
}
async function wrapPromise(value) {
  try {
    return Ok(await value);
  } catch (e) {
    return Err(e);
  }
}

// src/tinyassert.ts
function tinyassert(value, message) {
  if (value) {
    return;
  }
  if (message instanceof Error) {
    throw message;
  }
  throw new TinyAssertionError(message, tinyassert);
}
var TinyAssertionError = class _TinyAssertionError extends Error {
  constructor(message, stackStartFunction) {
    super(message);
    if ("captureStackTrace" in Error) {
      Error.captureStackTrace(this, stackStartFunction ?? _TinyAssertionError);
    }
  }
};

// src/misc.ts
function assertUnreachable(value) {
  throw new Error("assertUnreachable", { cause: value });
}
var typedBoolean = Boolean;
function arrayToEnum(values) {
  return Object.fromEntries(values.map((v) => [v, v]));
}
function includesGuard(ls, v) {
  return ls.includes(v);
}
function safeFunctionCast(f) {
  return f;
}

// src/lodash.ts
function range(start, end) {
  if (typeof end === "undefined") {
    return Array.from(Array(start), (_, i) => i);
  }
  return Array.from(Array(end - start), (_, i) => i + start);
}
function sortBy(ls, ...keyFns) {
  return sortByMap(ls, (x) => keyFns.map((f) => f(x)), arrayCompareFn);
}
function splitByChunk(ls, size) {
  if (!(Number.isInteger(size) && size > 0)) {
    return [];
  }
  let result = [];
  for (let i = 0; i < ls.length; i += size) {
    result.push(ls.slice(i, i + size));
  }
  return result;
}
function groupBy(ls, f) {
  const result = /* @__PURE__ */ new Map();
  for (const x of ls) {
    const y = f(x);
    if (!result.has(y)) {
      result.set(y, []);
    }
    result.get(y).push(x);
  }
  return result;
}
function mapKeys(map, f) {
  return new Map([...map].map(([k, v]) => [f(v, k), v]));
}
function mapValues(map, f) {
  return new Map([...map].map(([k, v]) => [k, f(v, k)]));
}
function mapGroupBy(ls, keyFn, valueFn) {
  return mapValues(groupBy(ls, keyFn), valueFn);
}
function pickBy(map, f) {
  return new Map([...map].filter(([k, v]) => f(v, k)));
}
function uniq(ls) {
  return Array.from(new Set(ls));
}
function uniqBy(ls, f) {
  const ys = /* @__PURE__ */ new Set();
  return ls.filter((x) => {
    const y = f(x);
    if (ys.has(y)) {
      return false;
    }
    ys.add(y);
    return true;
  });
}
function partition(ls, f) {
  const result = [[], []];
  for (const x of ls) {
    result[f(x) ? 0 : 1].push(x);
  }
  return result;
}
function zip(ls1, ls2) {
  return range(Math.min(ls1.length, ls2.length)).map((i) => [ls1[i], ls2[i]]);
}
function zipMax(ls1, ls2) {
  return range(Math.max(ls1.length, ls2.length)).map((i) => [ls1[i], ls2[i]]);
}
function difference(ls1, ls2) {
  const set2 = new Set(ls2);
  return ls1.filter((e) => !set2.has(e));
}
function intersection(ls1, ls2) {
  if (ls1.length > ls2.length) {
    [ls1, ls2] = [ls2, ls1];
  }
  const set2 = new Set(ls2);
  return ls1.filter((e) => set2.has(e));
}
function capitalize(s) {
  return s.slice(0, 1).toUpperCase() + s.slice(1);
}
function splitFirst(s, sep) {
  let i = s.indexOf(sep);
  if (i === -1) {
    i = s.length;
  }
  return [s.slice(0, i), s.slice(i + sep.length)];
}
function splitLast(s, sep) {
  let i = s.lastIndexOf(sep);
  if (i === -1) {
    i = s.length;
  }
  return [s.slice(0, i), s.slice(i + sep.length)];
}
function isNil(value) {
  return value === null || typeof value === "undefined";
}
function isNotNil(value) {
  return !isNil(value);
}
function once(f) {
  let result;
  let called = false;
  function wrapper(...args) {
    if (!called) {
      result = f(...args);
      called = true;
    }
    return result;
  }
  return wrapper;
}
function memoize(f, options) {
  const keyFn = options?.keyFn ?? ((...args) => args[0]);
  const cache = options?.cache ?? /* @__PURE__ */ new Map();
  return safeFunctionCast((...args) => {
    const key = keyFn(...args);
    const value = cache.get(key);
    if (typeof value !== "undefined") {
      return value;
    }
    const newValue = f(...args);
    cache.set(key, newValue);
    return newValue;
  });
}
function debounce(f, ms, options) {
  let subscription;
  function wrapper(...args) {
    cancel();
    subscription = setTimeout(() => {
      subscription = void 0;
      f.apply(this, args);
      options?.onFinish?.();
    }, ms);
    options?.onStart?.();
  }
  function cancel() {
    if (typeof subscription !== "undefined") {
      clearTimeout(subscription);
      subscription = void 0;
    }
    options?.onCancel?.();
  }
  return Object.assign(wrapper, { cancel });
}
function delay(f, ms) {
  const subscriptions = /* @__PURE__ */ new Set();
  function wrapper(...args) {
    const sub = setTimeout(() => {
      subscriptions.delete(sub);
      f.apply(this, args);
    }, ms);
    subscriptions.add(sub);
  }
  function cancel() {
    for (const sub of subscriptions) {
      clearTimeout(sub);
    }
    subscriptions.clear();
  }
  return Object.assign(wrapper, { cancel });
}
function objectPick(o, keys) {
  return objectPickBy(o, (_v, k) => keys.includes(k));
}
function objectOmit(o, keys) {
  return objectPickBy(o, (_v, k) => !keys.includes(k));
}
function objectPickBy(o, f) {
  return objectFromEntries(objectEntries(o).filter(([k, v]) => f(v, k)));
}
function objectOmitBy(o, f) {
  return objectPickBy(o, (v, k) => !f(v, k));
}
function objectKeys(o) {
  return Object.keys(o);
}
function objectEntries(o) {
  return Object.entries(o);
}
function objectFromEntries(kvs) {
  return Object.fromEntries(kvs);
}
function objectMapEntries(o, f) {
  return objectFromEntries(objectEntries(o).map((kv) => f(kv)));
}
function objectMapValues(o, f) {
  return objectMapEntries(o, ([k, v]) => [k, f(v, k)]);
}
function objectMapKeys(o, f) {
  return objectMapEntries(o, ([k, v]) => [f(v, k), v]);
}
function objectHas(v, prop) {
  return Boolean(v && typeof v === "object" && prop in v);
}
function sortByMap(ls, mapFn, compareFn) {
  const mapped = ls.map(mapFn);
  function indexCompareFn(i, j) {
    return compareFn(mapped[i], mapped[j]);
  }
  return range(ls.length).sort(indexCompareFn).map((i) => ls[i]);
}
function anyCompareFn(x, y) {
  return x < y ? -1 : x > y ? 1 : 0;
}
function arrayCompareFn(xs, ys) {
  for (let i = 0; i < xs.length; i++) {
    const result = anyCompareFn(xs[i], ys[i]);
    if (result !== 0) {
      return result;
    }
  }
  return 0;
}

// src/promise.ts
function newPromiseWithResolvers() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}
async function* mapToAsyncGenerator(values, f, { concurrency }) {
  const pendings = [];
  let index = 0;
  for (const value of values) {
    if (pendings.length >= concurrency) {
      yield (await Promise.race(pendings))();
    }
    const promise = (async () => {
      const result = await f(value, index++);
      return () => {
        pendings.splice(pendings.indexOf(promise), 1);
        return result;
      };
    })();
    pendings.push(promise);
  }
  while (pendings.length > 0) {
    yield (await Promise.race(pendings))();
  }
}
async function arrayFromAsyncGenerator(generator) {
  const result = [];
  for await (const v of generator) {
    result.push(v);
  }
  return result;
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(), ms));
}

// src/hash.ts
function hashInt32(x) {
  x ^= x >>> 16;
  x = Math.imul(x, 569420461);
  x ^= x >>> 15;
  x = Math.imul(x, 3545902487);
  x ^= x >>> 15;
  return x >>> 0;
}
var HashRng = class {
  state;
  constructor(seed) {
    this.state = hashInt32(seed | 1);
  }
  int32() {
    return this.state = hashInt32(this.state);
  }
  float() {
    return this.int32() / 2 ** 32;
  }
};
function hashString(input, n = 4, textEncoder = new TextEncoder()) {
  let h = "";
  let seed = new Uint32Array([1779033703]);
  const key = textEncoder.encode(input);
  for (let i = 0; i < n; i++) {
    h += murmur3_32(key, seed[0]).toString(16).padStart(8, "0");
    seed[0] = murmur3_32(new Uint8Array(seed.buffer), 0);
  }
  return h;
}
function murmur3_32(key, seed) {
  const len = key.length;
  let h = seed;
  let i = 0;
  while (i < len - len % 4) {
    const k = key[i++] | key[i++] << 8 | key[i++] << 16 | key[i++] << 24;
    h ^= murmur3_32_scramble(k);
    h = h << 13 | h >>> 19;
    h = (Math.imul(h, 5) >>> 0) + 3864292196;
    h >>>= 0;
  }
  h ^= murmur3_32_scramble(key[i++] | key[i++] << 8 | key[i++] << 16);
  h ^= len;
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;
  return h >>> 0;
}
function murmur3_32_scramble(k) {
  k = Math.imul(k, 3432918353);
  k = k << 15 | k >>> 17;
  k = Math.imul(k, 461845907);
  return k >>> 0;
}

// src/regexp.ts
function regExpRaw({ raw }, ...params) {
  tinyassert(raw.length === params.length + 1);
  return new RegExp(
    [
      ...zip(
        raw,
        params.map((p) => p.source)
      ),
      raw.slice(-1)
    ].flat().join("")
  );
}
function escapeRegExp(input) {
  return input.replace(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&");
}
function mapRegExp(input, regex, onMatch, onNonMatch) {
  let lastIndex = 0;
  for (const m of input.matchAll(regex)) {
    tinyassert(typeof m.index === "number");
    if (lastIndex < m.index) {
      onNonMatch(input.slice(lastIndex, m.index));
    }
    onMatch(m);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < input.length) {
    onNonMatch(input.slice(lastIndex));
  }
}

// src/colors.ts
var codes = [
  // 3 bit fg/bg colors
  ["black", 30, 39],
  ["red", 31, 39],
  ["green", 32, 39],
  ["yellow", 33, 39],
  ["blue", 34, 39],
  ["magenta", 35, 39],
  ["cyan", 36, 39],
  ["white", 37, 39],
  ["bgBlack", 40, 49],
  ["bgRed", 41, 49],
  ["bgGreen", 42, 49],
  ["bgYellow", 43, 49],
  ["bgBlue", 44, 49],
  ["bgMagenta", 45, 49],
  ["bgCyan", 46, 49],
  ["bgWhite", 47, 49],
  // styles
  ["bold", 1, 22],
  ["dim", 2, 22],
  ["underline", 4, 24],
  ["inverse", 7, 27]
];
var colors = /* @__PURE__ */ (() => Object.fromEntries(
  codes.map(([name, start, end]) => [
    name,
    (v) => `\x1B[${start}m${v}\x1B[${end}m`
  ])
))();

// src/error.ts
function flattenErrorCauses(e) {
  let errors = [e];
  for (let i = 0; ; i++) {
    if (i > 100)
      throw new Error("bound loop just in case");
    if (e instanceof Error && e.cause && !errors.includes(e.cause)) {
      errors.push(e.cause);
      e = e.cause;
      continue;
    }
    break;
  }
  return errors;
}
function formatError(v, config) {
  const errors = config?.noCause ? [v] : flattenErrorCauses(v);
  const errorsString = errors.map((e, i) => {
    let label = "ERROR";
    if (i > 0) {
      label += ":CAUSE";
      if (i > 1) {
        label += `:${i}`;
      }
    }
    const e2 = e instanceof Error ? e : { message: String(e) };
    return formatErrorInner(label, e2, !config?.noColor);
  });
  return errorsString.join("\n");
}
function formatErrorInner(label, e, color) {
  let stack = e.stack?.split("\n").slice(1).join("\n") ?? "";
  if (color) {
    label = colors.bgRed(` ${label} `);
    stack = stack && colors.cyan(stack);
  } else {
    label = `[${label}]`;
  }
  return `
${label} ${e.message}

${stack && stack + "\n"}`;
}

// src/cache.ts
var TtlCache = class {
  constructor(ttlMs) {
    this.ttlMs = ttlMs;
  }
  _map = /* @__PURE__ */ new Map();
  get(k) {
    const found = this._map.get(k);
    if (typeof found !== "undefined") {
      const { t, v } = found;
      if (Date.now() < t + this.ttlMs) {
        return v;
      }
      this._map.delete(k);
    }
    return;
  }
  set(k, v) {
    this._map.set(k, { t: Date.now(), v });
  }
};
var LruCache = class {
  constructor(maxSize) {
    this.maxSize = maxSize;
  }
  // expose to allow probing internal e.g. for initializing cache
  _map = /* @__PURE__ */ new Map();
  get(k) {
    if (this._map.has(k)) {
      const v = this._map.get(k);
      this._map.delete(k);
      this._map.set(k, v);
    }
    return this._map.get(k);
  }
  set(k, v) {
    this._map.set(k, v);
    this.fixSize();
  }
  fixSize() {
    while (this._map.size > this.maxSize) {
      const next = this._map.keys().next();
      if (next.done) {
        break;
      }
      this._map.delete(next.value);
    }
  }
};
export {
  DefaultMap,
  Err,
  HashKeyDefaultMap,
  HashKeyMap,
  HashRng,
  LruCache,
  Ok,
  TinyAssertionError,
  TtlCache,
  UncheckedMap,
  arrayFromAsyncGenerator,
  arrayToEnum,
  assertUnreachable,
  capitalize,
  colors,
  debounce,
  defaultDict,
  delay,
  difference,
  escapeRegExp,
  flattenErrorCauses,
  formatError,
  groupBy,
  hashInt32,
  hashString,
  includesGuard,
  intersection,
  isNil,
  isNotNil,
  mapGroupBy,
  mapKeys,
  mapOption,
  mapRegExp,
  mapToAsyncGenerator,
  mapValues,
  memoize,
  murmur3_32,
  newPromiseWithResolvers,
  objectEntries,
  objectFromEntries,
  objectHas,
  objectKeys,
  objectMapKeys,
  objectMapValues,
  objectOmit,
  objectOmitBy,
  objectPick,
  objectPickBy,
  okToOption,
  once,
  partition,
  pickBy,
  range,
  regExpRaw,
  safeFunctionCast,
  sleep,
  sortBy,
  splitByChunk,
  splitFirst,
  splitLast,
  tinyassert,
  typedBoolean,
  uniq,
  uniqBy,
  wrapError,
  wrapErrorAsync,
  wrapPromise,
  zip,
  zipMax
};
